import { differenceInDays } from 'date-fns';
import { DatesRange } from '@utils/types/calendar';
import { FeaturedListingType } from '@utils/types/featuredListing';
import { toDollars } from '@utils/money';

export const calculateNightlyPrice = ({
  dates,
  price_for_selected_dates,
}: {
  dates: DatesRange;
  price_for_selected_dates: number;
}) => {
  const totalDays = differenceInDays(dates.to!, dates.from!);

  return price_for_selected_dates / totalDays;
};

export const calculateMonthlyPrice = ({
  dates,
  price_for_selected_dates,
}: {
  dates: DatesRange;
  price_for_selected_dates: number;
}) => {
  const totalDays = differenceInDays(dates.to!, dates.from!);

  if (totalDays < 30) {
    return calculateNightlyPrice({
      dates,
      price_for_selected_dates,
    });
  }

  const totalMonths = totalDays / 30;

  return Math.round((price_for_selected_dates / totalMonths) * 100) / 100;
};

export const calculateMinStayPrice = ({
  next_available_min_stay_price,
  average_min_stay_price,
  min_stay,
}: Pick<
  FeaturedListingType,
  'next_available_min_stay_price' | 'average_min_stay_price' | 'min_stay'
>) => {
  const price = next_available_min_stay_price || average_min_stay_price || 0;

  if (min_stay < 30) {
    return price / min_stay;
  }

  return price / Math.ceil(min_stay / 30);
};

export const calculateAverageTotalPerMonth = (
  total: number,
  numberOfNights: number
) => {
  return Math.round((total / numberOfNights) * 30 * 100) / 100;
};

export const calculateAverageTotalPerNight = (
  total: number,
  numberOfNights: number
) => {
  return Math.round((total / numberOfNights) * 100) / 100;
};

export const getMinStayPrice = (
  listing: Pick<
    FeaturedListingType,
    'next_available_min_stay_price' | 'average_min_stay_price' | 'min_stay'
  >,
  withFormatting = true
) => {
  const price =
    listing.next_available_min_stay_price ||
    listing.average_min_stay_price ||
    0;

  if (!withFormatting) {
    if (listing.min_stay < 30) {
      return toDollars(price / listing.min_stay, 0);
    }
    return toDollars(price / Math.ceil(listing.min_stay / 30), 0);
  }

  if (listing.min_stay < 30) {
    return `${toDollars(price / listing.min_stay, 0)} / night`;
  }

  const months = Math.ceil(listing.min_stay / 30);
  return `${toDollars(price / months, 0)} / month`;
};
